import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";

import { ExistingDestination, prepareDestinationFromExisting, useDestinationForm } from "@prequel/react";
import { ConfirmModal, MenuItemStyle, Modal, Spinner } from "@prequel/shared-components";

import SyncSpecificModelsModal from "../SyncSpecificModelsModal";

import { useCreateDataIntegrityJob } from "@/api/queries/data-integrity";
import { useDeleteDestination, useDestinationEnabledModels } from "@/api/queries/destinations";
import { useOrg } from "@/api/queries/orgs";
import { usePostTransfer } from "@/api/queries/transfers";
import ConfirmFullRefreshModal from "@/components/ConfirmFullRefreshModal";
import DangerZone from "@/components/DangerZone";
import { env } from "@/env";

type DestinationAdminProps = {
  destination: ExistingDestination;
};

const DestinationAdmin = () => {
  const { destination: existingDestination } = useOutletContext<DestinationAdminProps>();
  const navigate = useNavigate();
  const destinationID = existingDestination.id;
  const { data: org } = useOrg();
  const { data: fetchedModels } = useDestinationEnabledModels(destinationID);
  const deleteDestinationMutation = useDeleteDestination();
  const postTransferMutation = usePostTransfer();
  const createIntegrityJobMutation = useCreateDataIntegrityJob();

  const destination = prepareDestinationFromExisting(existingDestination);
  const form = useDestinationForm(destination, org?.id ?? "", {
    includeInternalFields: true,
    host: env.VITE_APP_API_SERVER,
  });

  const vendorSupportsDataIntegrityChecks = useMemo(() => {
    return [
      "athena",
      "bigquery",
      "clickhouse",
      "postgres",
      "aurora_postgres",
      "databricks",
      "mysql",
      "aurora_mysql",
      "oracle",
      "redshift",
      "snowflake",
      "sql_server",
    ].includes(destination.vendor);
  }, [destination.vendor]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showSelectiveRefreshModal, setShowSelectiveRefreshModal] = useState(false);
  const [showIntegrityModal, setShowIntegrityModal] = useState(false);

  const enabledModels = fetchedModels ?? [];

  const onDelete = () => {
    setShowDeleteModal(true);
  };

  const onFullRefresh = () => {
    setShowRefreshModal(true);
  };

  const onDataIntegrityCheck = () => {
    setShowIntegrityModal(true);
  };

  const onConfirmDelete = () => {
    setShowDeleteModal(false);
    if (existingDestination) {
      deleteDestinationMutation.mutate({
        destinationID: existingDestination.id,
        redirectPath: "/destinations",
      });
    }
  };

  const onConfirmForceRefresh = () => {
    setShowRefreshModal(false);
    if (existingDestination) {
      postTransferMutation.mutate({
        destinationID: existingDestination.id,
        fullRefresh: true,
      });
    }
  };

  const onConfirmIntegrityCheck = () => {
    setShowIntegrityModal(false);
    if (existingDestination) {
      createIntegrityJobMutation.mutate({
        destinationID: existingDestination.id,
        modelNames: enabledModels.map((model) => model.model_name),
        checkValueIntegrity: false,
      });
    }
  };

  const refreshButtonMenuItems = [
    {
      label: "Select models to full refresh",
      style: MenuItemStyle.PRIMARY,
      onClick: () => setShowSelectiveRefreshModal(true),
    },
  ];

  if (!form) {
    return <Spinner />;
  }

  const dangerZoneActions = [
    {
      label: "Edit destination",
      description: "Edit destination fields. Destination 'vendor, host, and recipient_id' cannot be changed.",
      buttonText: "Edit destination",
      onClick: () => navigate(`/destinations/${destinationID}/edit`),
    },
    {
      label: "Delete destination",
      description: "Once you delete a destination, the action cannot be undone.",
      buttonText: "Delete destination",
      onClick: onDelete,
    },
    {
      label: "Force full refresh for destination",
      description:
        "A full refresh will delete all existing data in the destination and re-sync all available data. This process may take a while.",
      buttonText: "Force full refresh",
      onClick: onFullRefresh,
      menuItems: refreshButtonMenuItems,
    },
    {
      label: "Run data integrity check",
      description: "Compare data in your source and destination to identify any discrepancies.",
      buttonText: "Run data integrity check",
      onClick: onDataIntegrityCheck,
      hidden: !vendorSupportsDataIntegrityChecks,
    },
  ];

  return (
    <div className="mt-6 space-y-10">
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ConfirmModal
          title="Delete destination"
          message="Are you sure you want to delete this destination? This action cannot be undone."
          onConfirm={onConfirmDelete}
          confirmButtonText="Delete"
          onCancel={() => setShowDeleteModal(false)}
        />
      </Modal>
      <ConfirmFullRefreshModal
        show={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        onConfirm={onConfirmForceRefresh}
      />
      <Modal open={showIntegrityModal} onClose={() => setShowIntegrityModal(false)}>
        <ConfirmModal
          title="Run data integrity check"
          message="Are you sure you want to run a data integrity check? This will compare data from all models and may take a while."
          onConfirm={onConfirmIntegrityCheck}
          confirmButtonText="Run check"
          onCancel={() => setShowIntegrityModal(false)}
        />
      </Modal>
      <Modal open={showSelectiveRefreshModal} onClose={() => setShowSelectiveRefreshModal(false)}>
        <SyncSpecificModelsModal
          destination={existingDestination}
          fullRefresh={true}
          onCancel={() => setShowSelectiveRefreshModal(false)}
          onConfirm={() => setShowSelectiveRefreshModal(false)}
        />
      </Modal>
      <DangerZone actions={dangerZoneActions} />
    </div>
  );
};

export default DestinationAdmin;
